import { useState, useEffect } from 'react';
import axios from 'axios';

const PromptPreference = () => {
  const [selectedOrgId, setSelectedOrgId] = useState('');
  const [prompts, setPrompts] = useState([]);
  const [selectedPrompts, setSelectedPrompts] = useState([]);
  const [postCount, setPostCount] = useState(0);
  const [organizations, setOrganizations] = useState([]);
  const [selectedTimeline, setSelectedTimeline] = useState('all');
  const [statusMsg, setStatusMsg] = useState('');

  useEffect(() => {
    const fetchOrganizations = async () => {
      try {
        const response = await axios.get('https://genz-staging.feeltiptop.com/api/social_glass/organizations', {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
          },
        });
        setOrganizations(response.data);
      } catch (error) {
        console.error('Error fetching organizations:', error);
      }
    };
    fetchOrganizations();
  }, []);

  useEffect(() => {
    const fetchOrgData = async () => {
      if (!selectedOrgId) return;

      try {
        const preferencesResponse = await axios.get('https://genz-staging.feeltiptop.com/api/social_glass/getFeedPreferences', {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
          },
        });
        const orgPreferences = preferencesResponse.data.find(p => p.organization_id === parseInt(selectedOrgId));

        if (orgPreferences) {
          setSelectedPrompts(orgPreferences.prompts || []);
          setSelectedTimeline(orgPreferences.timeline || 'all');

          setPostCount(orgPreferences.preference_type === 'prompt' ? orgPreferences.post_count || 0 : 0);
        }

        const promptsResponse = await axios.get('https://genz-staging.feeltiptop.com/api/social_glass/prompts', {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
          },
          params: { organization_id: selectedOrgId },
        });
        setPrompts(promptsResponse.data);
      } catch (error) {
        console.error('Error fetching prompts or preferences:', error);
      }
    };
    fetchOrgData();
  }, [selectedOrgId]);

  const handleOrgChange = (e) => {
    setSelectedOrgId(e.target.value);
    setSelectedPrompts([]);
    setPostCount(0);
    setStatusMsg('');
  };

  const handlePromptSelection = (e) => {
    const { value, checked } = e.target;
    const promptID = parseInt(value);
    setSelectedPrompts((prev) =>
      checked ? [...prev, promptID] : prev.filter((id) => id !== promptID)
    );
  };

  const fetchPostCount = async (promptIds) => {
    if (promptIds.length === 0) return;
    try {
      const response = await axios.get('https://genz-staging.feeltiptop.com/api/social_glass/posts/count', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        params: {
          prompt_ids: promptIds.join(','),
          timeline: selectedTimeline
        },
      });
      setPostCount(response.data.totalPosts);
    } catch (error) {
      console.error('Error fetching post count:', error);
    }
  };

  const savePreferences = async (updatedPrompts) => {
    if (updatedPrompts.length === 0) return;
    try {
      await axios.post('https://genz-staging.feeltiptop.com/api/social_glass/saveFeedPreferences', {
        organization_id: selectedOrgId,
        prompt_ids: updatedPrompts,
        timeline: selectedTimeline,
      }, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });
      setStatusMsg('✅ Preferences saved successfully.');
    } catch (error) {
      setStatusMsg('❌ Error saving preferences.');
      console.error('Error saving preferences:', error);
    }
  };

  const handleUpdatePreferences = async () => {
    await savePreferences(selectedPrompts);
    await fetchPostCount(selectedPrompts);
  };

  return (
    <div className="min-h-screen max-w-4xl mx-auto p-6 bg-white rounded-xl shadow-md space-y-6">
      <h1 className="text-2xl font-bold text-gray-800">Feed Preferences</h1>

      <div className="space-y-2">
        <label htmlFor="organization" className="block font-medium font-semibold">Select Organization:</label>
        <select
          id="organization"
          className="w-full p-2 border border-gray-300 rounded"
          value={selectedOrgId}
          onChange={handleOrgChange}
        >
          <option value="">Choose an Organization</option>
          {organizations.map((org) => (
            <option key={org.OrganizationID} value={org.OrganizationID}>
              {org.Name}
            </option>
          ))}
        </select>
      </div>

      {selectedOrgId && (
        <div className="space-y-4">
          <h2 className="text-lg font-semibold text-gray-700">Select Prompts:</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {prompts.length > 0 ? (
              prompts.map((prompt) => (
                <label key={prompt.PromptID} className="flex items-start sm:items-center space-x-2">
                  <input
                    type="checkbox"
                    value={prompt.PromptID}
                    checked={selectedPrompts.includes(prompt.PromptID)}
                    onChange={handlePromptSelection}
                    className="mt-1 sm:mt-0"
                  />
                  <span className="text-gray-700 break-words max-w-full">
                    {prompt.PromptText}
                  </span>
                </label>
              ))
            ) : (
              <p className="text-sm text-gray-500">No prompts found for this organization.</p>
            )}
          </div>
        </div>
      )}

      <div className="space-y-2">
        <label htmlFor="timeline" className="block font-medium font-semibold">Select Timeline:</label>
        <select
          id="timeline"
          className="w-full p-2 border border-gray-300 rounded"
          value={selectedTimeline}
          onChange={(e) => setSelectedTimeline(e.target.value)}
        >
          <option value="day">Last Day</option>
          <option value="week">Last 7 Days</option>
          <option value="month">Last 30 Days</option>
          <option value="all">All Time</option>
        </select>
      </div>

      <div className="text-gray-700 font-medium">
        Total Posts for Selected Prompts: <span className="font-bold">{postCount}</span>
      </div>

      <button
        className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 transition"
        onClick={handleUpdatePreferences}
      >
        Update Preferences
      </button>

      {statusMsg && (
        <div className={`text-sm p-2 rounded ${statusMsg.includes('✅') ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
          {statusMsg}
        </div>
      )}
    </div>
  );
};

export default PromptPreference;
